
import { PythonModule } from '../types';

export const PYTHON_ADVANCED_DATA_STRUCTURES_MODULE: PythonModule = {
  id: "python-advanced-data-structures",
  title: "Advanced Data Structures and Collections",
  description: "Master Python's advanced data structures including sets, deques, named tuples, and custom collections.",
  difficulty: "intermediate",
  content: `
# Advanced Data Structures and Collections

Python provides powerful built-in data structures beyond basic lists and dictionaries.

## Sets and Set Operations

Sets are unordered collections of unique elements.

### Basic Set Operations

\`\`\`python
# Creating sets
fruits = {"apple", "banana", "orange"}
numbers = set([1, 2, 3, 4, 5])
empty_set = set()  # Note: {} creates an empty dict, not set

# Adding and removing elements
fruits.add("grape")
fruits.remove("banana")  # Raises KeyError if not found
fruits.discard("banana")  # No error if not found

# Set operations
set1 = {1, 2, 3, 4}
set2 = {3, 4, 5, 6}

union = set1 | set2  # {1, 2, 3, 4, 5, 6}
intersection = set1 & set2  # {3, 4}
difference = set1 - set2  # {1, 2}
symmetric_diff = set1 ^ set2  # {1, 2, 5, 6}
\`\`\`

### Practical Set Applications

\`\`\`python
# Remove duplicates from list
numbers = [1, 2, 2, 3, 3, 3, 4]
unique_numbers = list(set(numbers))

# Check membership efficiently
valid_statuses = {"active", "inactive", "pending"}
user_status = "active"
if user_status in valid_statuses:
    print("Valid status")

# Find common elements between lists
list1 = [1, 2, 3, 4, 5]
list2 = [4, 5, 6, 7, 8]
common = list(set(list1) & set(list2))
\`\`\`

## Collections Module

### Counter

\`\`\`python
from collections import Counter

# Count elements in a sequence
text = "hello world"
letter_count = Counter(text)
print(letter_count)  # Counter({'l': 3, 'o': 2, 'h': 1, 'e': 1, ' ': 1, 'w': 1, 'r': 1, 'd': 1})

# Most common elements
words = ["apple", "banana", "apple", "orange", "banana", "apple"]
word_count = Counter(words)
print(word_count.most_common(2))  # [('apple', 3), ('banana', 2)]

# Counter arithmetic
counter1 = Counter(['a', 'b', 'c', 'a'])
counter2 = Counter(['a', 'b', 'b'])
print(counter1 + counter2)  # Counter({'a': 3, 'b': 3, 'c': 1})
\`\`\`

### defaultdict

\`\`\`python
from collections import defaultdict

# Automatically initialize missing keys
dd = defaultdict(list)
dd['fruits'].append('apple')
dd['fruits'].append('banana')
dd['vegetables'].append('carrot')

# Group items
students = [
    ('Alice', 'Math'),
    ('Bob', 'Physics'),
    ('Alice', 'Chemistry'),
    ('Charlie', 'Math')
]

groups = defaultdict(list)
for student, subject in students:
    groups[subject].append(student)
\`\`\`

### deque (Double-ended queue)

\`\`\`python
from collections import deque

# Efficient operations at both ends
dq = deque([1, 2, 3])
dq.appendleft(0)  # deque([0, 1, 2, 3])
dq.append(4)      # deque([0, 1, 2, 3, 4])
dq.popleft()      # Returns 0, deque([1, 2, 3, 4])
dq.pop()          # Returns 4, deque([1, 2, 3])

# Rotating elements
dq.rotate(1)      # deque([3, 1, 2])
dq.rotate(-1)     # deque([1, 2, 3])

# Maximum length deque (circular buffer)
circular_buffer = deque(maxlen=3)
for i in range(5):
    circular_buffer.append(i)
print(circular_buffer)  # deque([2, 3, 4], maxlen=3)
\`\`\`

### namedtuple

\`\`\`python
from collections import namedtuple

# Create a named tuple class
Point = namedtuple('Point', ['x', 'y'])
p1 = Point(1, 2)
print(p1.x, p1.y)  # 1 2

# Person example
Person = namedtuple('Person', ['name', 'age', 'city'])
john = Person('John', 30, 'New York')
print(f"{john.name} is {john.age} years old")

# Convert to dict
print(john._asdict())  # {'name': 'John', 'age': 30, 'city': 'New York'}

# Create new instance with some fields changed
older_john = john._replace(age=31)
\`\`\`

## Best Practices

1. **Choose the right data structure** for your use case
2. **Use sets for membership testing** and unique collections
3. **Use deque for queue operations** and front/back access
4. **Use Counter for counting** and frequency analysis
5. **Use defaultdict to avoid KeyError** in grouping operations
6. **Consider memory usage** vs performance trade-offs

## Practice Exercise

Implement a text analysis tool that uses various data structures to count words, find unique words, track word frequencies, and identify the most common phrases.
`,
  duration: "65 minutes",
  objectives: [
    "Master advanced Python data structures",
    "Understand when to use sets, deques, and counters",
    "Implement custom data structures",
    "Analyze performance characteristics",
    "Apply collections module effectively"
  ],
  order: 5
};
